import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE } from './constants'
import { IDocumentsState, IDocument } from './types'

const initialState: IDocumentsState = {
  byClassKey: {},
  tags: {},
  lastRemovedDocumentId: null,
}

const getClassKey = (className: string, classId: string) => `${className}:${classId}`

const slice = createSlice({
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    // Document list actions
    documents_request: (state, { payload }: PayloadAction<{ className: string; classId: string }>) => {},
    documents_success: (
      state,
      { payload }: PayloadAction<{ className: string; classId: string; documents: IDocument[] }>,
    ) => {
      const key = getClassKey(payload.className, payload.classId)
      state.byClassKey[key] = {
        className: payload.className,
        classId: payload.classId,
        documents: payload.documents,
      }
    },

    // Document tags actions
    documentTags_request: (state, { payload }: PayloadAction<{ documentId: string }>) => {},
    documentTags_success: (state, { payload }: PayloadAction<{ documentId: string; tags: Record<string, string> }>) => {
      state.tags[payload.documentId] = payload.tags
    },

    // Download document
    downloadDocument_request: (state, { payload }: PayloadAction<{ documentId: string }>) => {},
    downloadDocument_success: (state, { payload }: PayloadAction<{ documentId: string; downloadUrl: string }>) => {},

    // Remove document
    removeDocument_request: (
      state,
      { payload }: PayloadAction<{ documentId: string; className: string; classId: string }>,
    ) => {},
    removeDocument_success: (
      state,
      { payload }: PayloadAction<{ documentId: string; className: string; classId: string }>,
    ) => {
      // Remove document from the list
      const key = getClassKey(payload.className, payload.classId)
      if (state.byClassKey[key]) {
        state.byClassKey[key].documents = state.byClassKey[key].documents.filter(doc => doc.id !== payload.documentId)
      }
      // Remove tags
      delete state.tags[payload.documentId]
      // Track the last removed document ID
      state.lastRemovedDocumentId = payload.documentId
    },
    clearLastRemovedDocumentId: state => {
      state.lastRemovedDocumentId = null
    },
    // Rename document
    renameDocument_request: (
      state,
      { payload }: PayloadAction<{ documentId: string; name: string; className: string; classId: string }>,
    ) => {},
    renameDocument_success: (
      state,
      { payload }: PayloadAction<{ documentId: string; name: string; className: string; classId: string }>,
    ) => {
      // Update document name in the list
      const key = getClassKey(payload.className, payload.classId)
      if (state.byClassKey[key]) {
        const doc = state.byClassKey[key].documents.find(d => d.id === payload.documentId)
        if (doc) {
          doc.name = payload.name
        }
      }
    },

    // Update document tags
    updateDocumentTags_request: (
      state,
      { payload }: PayloadAction<{ documentId: string; tags: Record<string, string> }>,
    ) => {},
    updateDocumentTags_success: (
      state,
      { payload }: PayloadAction<{ documentId: string; tags: Record<string, string> }>,
    ) => {
      state.tags[payload.documentId] = payload.tags
    },
  },
})

const { reducer, actions: events } = slice

export type DocumentsEvent = ActionType<typeof events>

export { events, initialState, reducer, getClassKey }
